import { useQuery, type QueryClient } from '@tanstack/react-query'
import {
  aufgabenQuery,
  personenQuery,
  workspacesQuery,
  type Aufgabe,
  type Person,
  type WorkspaceEintrag,
} from './queries'
import { aktiverWorkspaceIst, workspaceSetzen } from './trpc'

/**
 * **Der Bestand dieser Arbeitswelt** – der Client-Teil der Trennung
 * ([Z-07](../../../../docs/phasen/zusatz-07-workspaces.md),
 * [E-234](../../../../docs/ENTSCHEIDUNGEN.md#e-234)).
 *
 * ## Warum überhaupt im Client gefiltert wird
 *
 * Leitplanke 2: der Aufgabenbestand liegt komplett im Browser und wird per WebSocket aktuell
 * gehalten. Er speist **beides** – „Mein Tag" und „Auf dem Schirm", die über alle Arbeitswelten
 * gehen, und „Alle Aufgaben", „Trichter" und die ⌘K-Palette, die gebunden sind. Eine Verengung
 * auf dem Server würde „Mein Tag" beim ersten Umschalten halb leeren.
 *
 * Also filtert der Server nach der **Grenze** (Mitgliedschaft) und der Client nach der
 * **Ansicht** (Umschalter) – ein Vergleich im Speicher statt eines zweiten Ladens, damit der
 * Wechsel unter 50 ms bleibt (Leitplanke 1).
 *
 * ## Warum das ein Hook ist und keine Funktion, die man aufruft
 *
 * Damit man es nicht vergessen kann, ohne dass es auffällt. Wer `useQuery(aufgabenQuery)`
 * schreibt, bekommt den **ungefilterten** Bestand – und in einer gebundenen Ansicht ist das
 * genau der Fehler, den Nils am 25.08.2026 gemeldet hat (*„die Trennung ist noch nicht wirklich
 * da"*). Der Hook heißt deshalb nach dem, was er liefert, und steht in jeder gebundenen
 * Ansicht an derselben Stelle, an der vorher die rohe Abfrage stand.
 *
 * **Ungefiltert bleibt richtig für:** „Mein Tag", „Auf dem Schirm", „Meine Beteiligungen". Das
 * sind die Ansichten des **Menschen**, und ein Mensch hat einen Tag
 * ([E-234](../../../../docs/ENTSCHEIDUNGEN.md#e-234)).
 */
export function useAufgabenDieserWelt(): { aufgaben: Aufgabe[]; laedt: boolean } {
  const { data: alle = [], isLoading } = useQuery(aufgabenQuery)
  const { data: workspaces } = useQuery(workspacesQuery)

  /**
   * **Kein Filter, solange es nur eine Arbeitswelt gibt.**
   *
   * Bei einem einzigen Workspace ist der Vergleich immer wahr – aber er wäre eine Zeile, die
   * bei einem Fehler in der Kennung stillschweigend **alles** ausblendet. Ist ohnehin nichts zu
   * trennen, wird auch nicht getrennt: das ist der Zustand direkt nach der Migration, und er
   * soll aussehen wie vorher.
   */
  const aktiv = aktiverWorkspaceIst()
  if (!aktiv || (workspaces?.length ?? 0) < 2) return { aufgaben: alle, laedt: isLoading }

  return { aufgaben: alle.filter((a) => a.workspaceId === aktiv), laedt: isLoading }
}

/**
 * **Alle Aufgaben, die ich sehen darf – über alle meine Arbeitswelten**
 * ([Z-20](../../../../docs/phasen/zusatz-20-mein-tag-markieren.md), 05.10.2026).
 *
 * Das Gegenstück zu {@link useAufgabenDieserWelt} für drei Stellen, an denen der Umschalter
 * ausdrücklich **nichts** zu sagen hat:
 *
 * | Wer | Warum |
 * |---|---|
 * | **⌘K-Suche** | Nils, 05.10.2026: *„Die Allgemeine Suche soll alle Workspaces durchsuchen können."* Wer sucht, weiß oft nicht, wo etwas liegt ([F-98](../../../../docs/OFFENE-FRAGEN.md#f-98)) |
 * | **Für Mein Tag markiert** | eine Markierung ist eine Aussage des Menschen, und ein Mensch hat einen Tag (E-234) |
 * | **Kalender** | zeigt, was ich mir vorgenommen habe – auch Pool-Aufgaben, die mir noch nicht gehören (F-97) |
 *
 * Mehr als die eigenen Welten liefert der Server ohnehin nicht (`tasks.all` hält die
 * Sichtgrenze, samt Einladungen und Gast-Ausnahmen). Steht hier und nicht im Bauteil, damit der
 * Wächter `welt.trennung.test.ts` scharf bleibt: roh liest den Bestand nur diese Datei.
 */
export function useAufgabenAllerWelten(): { aufgaben: Aufgabe[]; laedt: boolean } {
  const { data: alle = [], isLoading } = useQuery(aufgabenQuery)
  return { aufgaben: alle, laedt: isLoading }
}

/**
 * **Die Aufgaben eines Vorhabens** – über die Welten hinweg (30.09.2026,
 * [E-372](../../../../docs/ENTSCHEIDUNGEN.md#e-372)).
 *
 * Das Gegenstück zu {@link useAufgabenDieserWelt} für den einen Fall, in dem der Umschalter
 * nichts zu sagen hat: **die Zugehörigkeit zum Vorhaben ist die schärfere Bedingung.** Ein
 * Vorhaben liegt in genau einer Arbeitswelt, seine Aufgaben auch – der Weltfilter kann hier
 * also nichts hinzufügen, aber alles wegnehmen: im Detail eines Vorhabens aus einer *anderen*
 * Welt leerte er die ganze Liste.
 *
 * Steht hier und nicht im Bauteil, damit der Wächter (`welt.trennung.test.ts`) scharf bleibt:
 * `aufgabenQuery` roh zu lesen ist weiterhin nur dieser Datei erlaubt, die die Trennung selbst
 * macht. Wer die Ausnahme braucht, nimmt den Hook – und liest hier, warum es eine gibt.
 */
export function useAufgabenDesVorhabens(vorhabenId: string): Aufgabe[] {
  const { data: alle = [] } = useQuery(aufgabenQuery)
  return alle.filter((a) => a.vorhabenId === vorhabenId)
}

/**
 * **Die Menschen dieser Arbeitswelt** – die Auswahllisten für Bearbeiter, Wunsch-Entwickler
 * und Mit-Owner (26.08.2026).
 *
 * Nils: *„Ebenfalls müssen wir noch pro Workspace «Teams» machen können, dass nicht die
 * Entwickler in jedem vorgeschlagen werden … Wenn die einem Workspace nicht zugeordnet sind,
 * dann sieht man die da auch nicht."*
 *
 * ## Das Modell gab es längst – es fehlte an den Listen
 *
 * `workspace_mitglied` steht seit Etappe 1. Aber `people.all` lieferte alle, und jede
 * Auswahlliste bot das ganze Haus an – auch in einem persönlichen Workspace. Wer dort jemanden
 * eintrug, gab ihm eine Aufgabe, **die er nie zu sehen bekommt**: die Grenze hält, die Auswahl
 * log. Ein „Team je Workspace" ist damit keine neue Tabelle, sondern dieser Filter.
 *
 * ## Warum ohne aktiven Workspace ungefiltert
 *
 * Weil „Mein Tag" über alle geht und die Erwähnungsliste in einem Kommentar dort jeden treffen
 * darf, mit dem man zusammenarbeitet. Verengt wird, wo eine Arbeitswelt gilt.
 */
export function usePersonenDieserWelt(welt?: string | null): {
  personen: Person[]
  laedt: boolean
} {
  const { data: alle = [], isLoading } = useQuery(personenQuery)
  const { data: workspaces } = useQuery(workspacesQuery)

  /**
   * **Eine genannte Welt schlägt die aktive** (28.08.2026). Das Aufgabendetail nennt die Welt
   * **der Aufgabe** – sie muss nicht die sein, in der der Umschalter steht, und aus „Mein Tag"
   * heraus ist sie es selten. Ohne das bot die Auswahl Leute an, die die Aufgabe nie zu sehen
   * bekämen: die Zuweisung ginge durch und die Aufgabe verschwände für den Zugewiesenen.
   */
  const gemeint = welt ?? aktiverWorkspaceIst()
  if (!gemeint || (workspaces?.length ?? 0) < 2) return { personen: alle, laedt: isLoading }

  return {
    personen: alle.filter((p) => (p.workspaces ?? '').split(',').includes(gemeint)),
    laedt: isLoading,
  }
}

/**
 * **Die Arbeitswelt, in der gerade gearbeitet wird** – mit ihrer Art.
 *
 * Gebraucht überall dort, wo die Oberfläche wissen muss, ob sie in einem **persönlichen**
 * Workspace steht. Nils, 26.08.2026:
 *
 * > *„bei den privaten Workspaces, da ist bei der Aufgabenanlage noch die Möglichkeit die
 * > Felder auszuwählen, die ja bei privaten Aufgaben gar nicht gelten dürfen
 * > (Wunsch-Entwickler, Mit-Owner, usw.)"*
 *
 * Er hat recht, und es ist mehr als Kosmetik: ein persönlicher Workspace hat **genau ein**
 * Mitglied ([E-232](../../../../docs/ENTSCHEIDUNGEN.md#e-232)). „Nimmt ab" und „kommen in
 * Frage" beantworten Fragen, die dort niemand stellen kann – ein Feld mit genau einer
 * möglichen Antwort ist keine Wahl, sondern eine Attrappe.
 */
export function useAktiveWelt(): { welt: WorkspaceEintrag | null; persoenlich: boolean } {
  const { data: workspaces } = useQuery(workspacesQuery)
  const aktiv = aktiverWorkspaceIst()
  const welt = workspaces?.find((w) => w.id === aktiv) ?? workspaces?.[0] ?? null
  return { welt, persoenlich: welt?.art === 'persoenlich' }
}

/**
 * **Der Schlüssel, unter dem die zuletzt gewählte Arbeitswelt im Browser steht.**
 *
 * Steht hier und nicht im Umschalter, seit es zwei Stellen gibt, die umschalten
 * ([Z-07.10](../../../../docs/phasen/zusatz-07-workspaces.md)). Zwei Zeichenketten mit
 * demselben Inhalt sind eine Verabredung, die beim Umbenennen der einen zerbricht – und zwar
 * lautlos: der Umschalter merkte sich dann etwas, das der Dialog nicht findet.
 */
export const WELT_SPEICHER_SCHLUESSEL = 'netcon-work.workspace'

/**
 * **In eine andere Arbeitswelt wechseln** – die eine Stelle, an der das passiert.
 *
 * Drei Dinge gehören zusammen und dürfen nicht auseinanderlaufen:
 *
 * 1. die Kopfzeile `x-workspace` für alle künftigen Anfragen (`workspaceSetzen`),
 * 2. das Gedächtnis im Browser, damit der nächste Aufruf dort weitermacht,
 * 3. **alle** Abfragen verwerfen.
 *
 * Punkt 3 ist bewusst grob. Welche Antwort vom Workspace abhängt, weiß der Umschalter nicht –
 * und soll es nicht: eine Liste „diese Abfragen betrifft der Wechsel" wäre eine, die beim
 * nächsten neuen Endpunkt unvollständig ist, und dann zeigte eine Ansicht stillschweigend die
 * Zahlen der vorigen Arbeitswelt. Der Bestand liegt ohnehin im Client (Leitplanke 2).
 */
export function weltWechseln(id: string, queryClient: QueryClient): void {
  workspaceSetzen(id)
  try {
    globalThis.localStorage?.setItem(WELT_SPEICHER_SCHLUESSEL, id)
  } catch {
    /* Kein Speicher (privater Modus, Test) – dann eben ohne Gedächtnis. */
  }
  void queryClient.invalidateQueries()
}

/**
 * **Wer sich erwähnen lässt: jeder Erreichbare, in jeder Welt** (16.09.2026,
 * [E-355](../../../../docs/ENTSCHEIDUNGEN.md#e-355)).
 *
 * Nils: *„Martina finde ich bspw. nicht als @-Person, dass ich die markieren kann."* Sie stand
 * im Bestand – aktiv, Gast, mit Adresse aus dem Intranet –, aber die Erwähnungsliste kam aus
 * `usePersonenDieserWelt`, und in keiner Welt ist sie Mitglied. So wie alle Gäste: die
 * bekommen keine Mitgliedschaft (E-237). Seit Z-07 war damit genau der Kreis unerwähnbar, für
 * den die Erwähnung gedacht ist.
 *
 * Die Welt-Verengung ist für **Zuweisungen** richtig: wer nicht Mitglied ist, sähe die Aufgabe
 * nie. Für eine Erwähnung gilt das Gegenteil, und zwar ausdrücklich – **sie öffnet die eine
 * Aufgabe** über die Weltgrenze hinweg (E-317). Eine Auswahl, die die Eingeladenen nicht
 * anbietet, widerspricht der eigenen Regel.
 *
 * Erwähnbar bleibt, wer **erreichbar** ist – Konto oder Adresse (E-192); wer keins von beidem
 * hat, wird nicht angeredet.
 */
export function useErwaehnbarePersonen(): {
  id: string
  displayName: string
  hinweis?: string
}[] {
  const { data: alle = [] } = useQuery(personenQuery)
  return alle
    .filter((p) => p.isActive && (p.canLogin || p.email))
    .map((p) => ({
      id: p.id,
      displayName: p.displayName,
      /** Steht als blasser Zusatz in der Auswahl – „bekommt nur eine Mail". */
      ...(p.canLogin ? {} : { hinweis: 'per Mail' }),
    }))
}

/**
 * **Wer abnehmen kann: die Welt zuerst, dann alle anderen Erreichbaren** (16.09.2026,
 * [E-355](../../../../docs/ENTSCHEIDUNGEN.md#e-355)).
 *
 * Der Abnehmer ist ein **Feld**, keine Rolle (E-106): wer dort steht, sieht die Aufgabe – auch
 * ein Gast, der in keiner Welt Mitglied ist (`gastSichtbarkeit` in `rechte.ts`). Die
 * Welt-Verengung wäre hier also die falsche Grenze: sie versteckte genau die Fachbereichsleute,
 * die abnehmen sollen. Trotzdem stehen die Mitglieder **zuerst**, denn in neun von zehn Fällen
 * ist es einer von ihnen; der Rest folgt als zweite Gruppe.
 *
 * `weitere` enthält nur **Erreichbare** – ein Abnehmer ohne Konto und Adresse bekäme weder die
 * Erinnerung noch könnte er klicken; die Aufgabe hinge dann an jemandem, den nichts erreicht.
 */
export function usePersonenFuerAbnahme(welt?: string | null): {
  inWelt: Person[]
  weitere: Person[]
} {
  const { personen: inWelt } = usePersonenDieserWelt(welt)
  const { data: alle = [] } = useQuery(personenQuery)
  const drin = new Set(inWelt.map((p) => p.id))
  return {
    inWelt: inWelt.filter((p) => p.isActive),
    weitere: alle.filter((p) => p.isActive && !drin.has(p.id) && (p.canLogin || p.email)),
  }
}
